import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { BACKEND_URL } from '../config';
import SEO from '../components/SEO';

import {
  ArrowsClockwise,
  CheckCircle,
  CircleNotch,
  House,
  Sparkle,
  WarningCircle
} from '@phosphor-icons/react';
/**
 * BlueprintContinue Page
 * 
 * Intermediate page that claims a pending blueprint draft after signup/login
 * and redirects user to the top opportunity with full context.
 * 
 * Flow:
 * 1. Read pending_blueprint_claim from localStorage
 * 2. Call POST /api/blueprint-draft/claim
 * 3. Redirect to /opportunities with highlight param (tier-safe route)
 * 
 * Hardened for:
 * - All tiers (Basic/Free/Pro)
 * - Idempotent claim (clears storage after success/failure)
 * - Friendly error states
 */
const BlueprintContinue = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('claiming'); // 'claiming' | 'success' | 'error'
  const [message, setMessage] = useState('Unlocking your blueprint...');
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'expired' | 'not_found' | 'already_claimed' | 'generic'

  // Clear all blueprint-related storage
  const clearBlueprintStorage = () => {
    localStorage.removeItem('pending_blueprint_claim');
    localStorage.removeItem('post_auth_redirect');
    sessionStorage.removeItem('blueprint_draft_token');
    sessionStorage.removeItem('blueprint_claimed');
    sessionStorage.removeItem('claimed_process_id');
    sessionStorage.removeItem('highlight_opportunity_id');
  };

  useEffect(() => {
    const claimAndRedirect = async () => {
      try {
        // Get pending blueprint token
        const draftToken = localStorage.getItem('pending_blueprint_claim');
        
        if (!draftToken) {
          // No pending blueprint - redirect to dashboard (tier-safe)
          console.log('[BlueprintContinue] No pending blueprint found, redirecting to opportunities');
          navigate('/opportunities', { replace: true });
          return;
        }

        setMessage('Claiming your blueprint...');

        // Get organization ID if available
        const orgId = localStorage.getItem('activeOrganizationId');
        
        const headers = {
          'Content-Type': 'application/json'
        };
        if (orgId) {
          headers['X-Organization-Id'] = orgId;
        }

        // Call claim endpoint
        const response = await fetch(`${BACKEND_URL}/api/blueprint-draft/claim`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ draft_token: draftToken })
        });

        const data = await response.json();

        if (!response.ok) {
          // Determine error type for appropriate messaging
          const errorMsg = data.detail || 'Failed to claim blueprint';
          let errType = 'generic';
          
          if (errorMsg.includes('expired')) {
            errType = 'expired';
          } else if (errorMsg.includes('not found') || response.status === 404) {
            errType = 'not_found';
          } else if (errorMsg.includes('already claimed') || response.status === 409) {
            errType = 'already_claimed';
          }
          
          throw { message: errorMsg, type: errType };
        }

        console.log('[BlueprintContinue] Claim successful:', data);

        // Clear all blueprint storage on success (idempotent)
        clearBlueprintStorage();
        
        setStatus('success');
        setMessage('Blueprint unlocked! Redirecting...');

        // Store context for the destination page
        if (data.process_id) {
          sessionStorage.setItem('blueprint_claimed', 'true');
          sessionStorage.setItem('claimed_process_id', data.process_id);
          if (data.top_opportunity_id) {
            sessionStorage.setItem('highlight_opportunity_id', data.top_opportunity_id);
          }
        }

        // Short delay to show success state, then redirect to TIER-SAFE route
        setTimeout(() => {
          // Navigate to opportunities page (accessible to all tiers)
          if (data.top_opportunity_id) {
            navigate(`/opportunities?process=${data.process_id}&highlight=${data.top_opportunity_id}&unlocked=true`, { replace: true });
          } else if (data.process_id) {
            navigate(`/opportunities?process=${data.process_id}&unlocked=true`, { replace: true });
          } else {
            navigate('/opportunities?unlocked=true', { replace: true });
          }
        }, 1200);

      } catch (err) {
        console.error('[BlueprintContinue] Error:', err);
        setStatus('error');
        setError(err.message || 'Something went wrong');
        setErrorType(err.type || 'generic');
        
        // Clear storage on any error (idempotent - prevent loops)
        clearBlueprintStorage();
      }
    };

    claimAndRedirect();
  }, [navigate]);

  // Error state UI
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#08090c] flex items-center justify-center">
        <SEO title="Blueprint Error | Man & Machine" />
        
        {/* Background gradients */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#EF4444] rounded-full filter blur-[180px] opacity-10"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#6366F1] rounded-full filter blur-[150px] opacity-10"></div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 text-center max-w-md mx-auto px-6"
        >
          <div className="w-20 h-20 mx-auto rounded-2xl bg-[rgba(239,68,68,0.2)] border border-[rgba(239,68,68,0.3)] flex items-center justify-center mb-6">
            <WarningCircle size={36} className="text-[#EF4444]" />
          </div>

          <h1 className="text-2xl font-bold text-white mb-4">
            {errorType === 'expired' && 'Blueprint Expired'}
            {errorType === 'not_found' && 'Blueprint Not Found'}
            {errorType === 'already_claimed' && 'Already Claimed'}
            {errorType === 'generic' && 'Something Went Wrong'}
          </h1>

          <p className="text-[#A0AEC0] mb-8">
            {errorType === 'expired' && 'This blueprint has expired. Blueprints are valid for 48 hours after creation.'}
            {errorType === 'not_found' && 'We couldn\'t find this blueprint. It may have been removed or never existed.'}
            {errorType === 'already_claimed' && 'This blueprint has already been claimed. Check your opportunities page.'}
            {errorType === 'generic' && error}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
            >
              <ArrowsClockwise size={18} />
              Generate New Blueprint
            </Link>
            
            {errorType === 'already_claimed' && (
              <Link
                to="/opportunities"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
              >
                View Opportunities
              </Link>
            )}
            
            {errorType !== 'already_claimed' && (
              <Link
                to="/opportunities"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
              >
                <House size={18} />
                Go to Dashboard
              </Link>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // Loading/Success state UI
  return (
    <div className="min-h-screen bg-[#08090c] flex items-center justify-center">
      <SEO title="Unlocking Blueprint... | Man & Machine" />
      
      {/* Background gradients */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#6366F1] rounded-full filter blur-[180px] opacity-15"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#10B981] rounded-full filter blur-[150px] opacity-10"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 text-center max-w-md mx-auto px-6"
      >
        {/* Icon */}
        <div className="mb-8">
          {status === 'claiming' && (
            <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
              <CircleNotch size={36} className="text-white animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className="w-20 h-20 mx-auto rounded-2xl bg-[#10B981] flex items-center justify-center"
            >
              <CheckCircle size={36} className="text-white" />
            </motion.div>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-4">
          {status === 'claiming' && 'Unlocking Your Blueprint'}
          {status === 'success' && 'Blueprint Unlocked!'}
        </h1>

        {/* Message */}
        <p className="text-[#A0AEC0] mb-6">{message}</p>

        {/* Progress indicators for claiming state */}
        {status === 'claiming' && (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-3 text-sm text-[#6b7194]">
              <Sparkle size={16} className="text-[#6366F1]" />
              <span>Preparing your AI automation blueprint...</span>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default BlueprintContinue;
